import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, ImageIcon, Megaphone, Paperclip, Pin, Plus, ScrollText } from "lucide-react";
import { useMe } from "@/context/MeContext";
import { useApi, useFetch, errMsg } from "@/lib/api";
import { fmtDateTime, fmtLong, today } from "@/lib/dates";
import { Card, ConfirmDelete, Empty, ErrorBox, FormSheet, Loading, PageTitle } from "@/components/common";
import { Field } from "@/components/fields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function AnnouncementSheet({ open, onOpenChange, onSaved }) {
  const api = useApi();
  const [form, setForm] = useState({ title: "", body: "", pinned: false });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm({ title: "", body: "", pinned: false }); }, [open]);
  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) return toast.error("Compila titolo e testo");
    setBusy(true);
    try { await api("post", "/announcements", form); toast.success("Avviso pubblicato"); onOpenChange(false); onSaved(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <FormSheet open={open} onOpenChange={onOpenChange} title="Nuovo avviso">
      <form onSubmit={save} className="space-y-4" data-testid="announcement-form">
        <Field label="Titolo"><Input className="h-11" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="announcement-title" /></Field>
        <Field label="Testo"><Textarea rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="announcement-body" /></Field>
        <label className="flex items-center justify-between rounded-xl bg-secondary/60 px-4 py-3">
          <span className="text-sm font-semibold">Fissa in alto</span>
          <Switch checked={form.pinned} onCheckedChange={(v) => setForm({ ...form, pinned: v })} data-testid="announcement-pinned" />
        </label>
        <Button type="submit" disabled={busy} className="h-12 w-full rounded-full text-base" data-testid="announcement-submit">Pubblica</Button>
      </form>
    </FormSheet>
  );
}

function CircularSheet({ open, onOpenChange, onSaved }) {
  const api = useApi();
  const blank = { title: "", body: "", number: "", date: today() };
  const [form, setForm] = useState(blank);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setForm(blank); setFiles([]); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Inserisci il titolo");
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    files.forEach((f) => fd.append("files", f));
    setBusy(true);
    try { await api("post", "/circulars", fd); toast.success("Circolare pubblicata"); onOpenChange(false); onSaved(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <FormSheet open={open} onOpenChange={onOpenChange} title="Nuova circolare" description="Avvisi e circolari inviati dalla scuola">
      <form onSubmit={save} className="space-y-4" data-testid="circular-form">
        <Field label="Titolo"><Input className="h-11" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="circular-title" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Numero"><Input className="h-11" value={form.number} placeholder="Es. 124" onChange={(e) => setForm({ ...form, number: e.target.value })} data-testid="circular-number" /></Field>
          <Field label="Data"><Input type="date" className="h-11" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="circular-date" /></Field>
        </div>
        <Field label="Testo (facoltativo)"><Textarea rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="circular-body" /></Field>
        <Field label="Allegati" hint="PDF o foto, max 10 MB ciascuno">
          <Input type="file" multiple accept="application/pdf,image/*" className="h-11 pt-2" onChange={(e) => setFiles(Array.from(e.target.files || []))} data-testid="circular-files" />
        </Field>
        {files.length > 0 && <p className="text-xs text-muted-foreground" data-testid="circular-files-count">{files.length} file selezionati</p>}
        <Button type="submit" disabled={busy} className="h-12 w-full rounded-full text-base" data-testid="circular-submit">{busy ? "Caricamento…" : "Pubblica"}</Button>
      </form>
    </FormSheet>
  );
}

function AttachmentChip({ att }) {
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const open = async () => {
    const win = window.open("", "_blank");
    setBusy(true);
    try {
      const blob = await api("get", `/files/${att.id}`, null, { responseType: "blob" });
      const url = URL.createObjectURL(blob);
      if (win) win.location.href = url; else window.location.href = url;
    } catch (e) {
      if (win) win.close();
      toast.error("Impossibile aprire l'allegato");
    } finally {
      setBusy(false);
    }
  };
  const Icon = att.content_type.startsWith("image/") ? ImageIcon : FileText;
  return (
    <button onClick={open} disabled={busy} className="flex max-w-full items-center gap-2 rounded-xl border border-border bg-secondary/50 px-3 py-2 text-sm font-medium transition-colors hover:bg-secondary active:scale-95" data-testid={`attachment-${att.id}`}>
      <Icon className="h-4 w-4 shrink-0 text-primary" /><span className="truncate">{att.name}</span>
    </button>
  );
}

const Header = ({ count, label, onAdd, testId, isAdmin }) => (
  <div className="mb-3 flex items-center justify-between">
    <p className="text-sm font-medium text-muted-foreground">{count} {label}</p>
    {isAdmin && <Button size="sm" className="rounded-full" onClick={onAdd} data-testid={testId}><Plus className="mr-1 h-4 w-4" />Nuovo</Button>}
  </div>
);

export default function Bacheca() {
  const api = useApi();
  const { isAdmin } = useMe();
  const ann = useFetch("/announcements");
  const circ = useFetch("/circulars");
  const [sheet, setSheet] = useState(null);
  const remove = async (path, reload) => {
    try { await api("delete", path); toast.success("Eliminato"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (ann.error || circ.error) return <ErrorBox text={ann.error || circ.error} onRetry={() => { ann.reload(); circ.reload(); }} />;

  return (
    <div>
      <PageTitle title="Bacheca" subtitle="Avvisi della classe e circolari della scuola" />
      <Tabs defaultValue="avvisi">
        <TabsList className="mb-5 grid h-11 w-full grid-cols-2 rounded-full sm:max-w-sm">
          <TabsTrigger value="avvisi" className="rounded-full" data-testid="bacheca-tab-avvisi">Avvisi</TabsTrigger>
          <TabsTrigger value="circolari" className="rounded-full" data-testid="bacheca-tab-circolari">Circolari</TabsTrigger>
        </TabsList>
        <TabsContent value="avvisi">
          <Header count={ann.data?.length ?? 0} label="avvisi dei rappresentanti" onAdd={() => setSheet("ann")} testId="btn-add-announcement" isAdmin={isAdmin} />
          {!ann.data ? <Loading /> : ann.data.length === 0 ? <Empty icon={Megaphone} text="Nessun avviso." /> : (
            <div className="grid gap-3 md:grid-cols-2">
              {ann.data.map((a) => (
                <Card key={a.id} className={`fade-up ${a.pinned ? "border-primary/40" : ""}`} data-testid={`announcement-card-${a.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      {a.pinned && <span className="mb-1.5 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-bold uppercase text-primary"><Pin className="h-3 w-3" />Fissato</span>}
                      <h3 className="font-heading text-lg font-bold leading-snug">{a.title}</h3>
                    </div>
                    {isAdmin && <ConfirmDelete testId={`announcement-delete-${a.id}`} onConfirm={() => remove(`/announcements/${a.id}`, ann.reload)} />}
                  </div>
                  <p className="mt-1.5 whitespace-pre-line text-sm">{a.body}</p>
                  <p className="mt-3 text-xs text-muted-foreground">{a.author} · {fmtDateTime(a.created_at)}</p>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="circolari">
          <Header count={circ.data?.length ?? 0} label="circolari" onAdd={() => setSheet("circ")} testId="btn-add-circular" isAdmin={isAdmin} />
          {!circ.data ? <Loading /> : circ.data.length === 0 ? <Empty icon={ScrollText} text="Nessuna circolare." /> : (
            <div className="grid gap-3 md:grid-cols-2">
              {circ.data.map((c) => (
                <Card key={c.id} className="fade-up" data-testid={`circular-card-${c.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-xs font-bold uppercase tracking-wide text-primary">{c.number ? `Circolare n. ${c.number} · ` : ""}{fmtLong(c.date)}</p>
                      <h3 className="mt-1 font-heading text-lg font-bold leading-snug">{c.title}</h3>
                    </div>
                    {isAdmin && <ConfirmDelete testId={`circular-delete-${c.id}`} onConfirm={() => remove(`/circulars/${c.id}`, circ.reload)} />}
                  </div>
                  {c.body && <p className="mt-1.5 whitespace-pre-line text-sm">{c.body}</p>}
                  {c.attachments.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Paperclip className="mt-2 h-4 w-4 text-muted-foreground" />
                      {c.attachments.map((att) => <AttachmentChip key={att.id} att={att} />)}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
      <AnnouncementSheet open={sheet === "ann"} onOpenChange={(o) => !o && setSheet(null)} onSaved={ann.reload} />
      <CircularSheet open={sheet === "circ"} onOpenChange={(o) => !o && setSheet(null)} onSaved={circ.reload} />
    </div>
  );
}
